import { RotateCcw } from 'lucide-react';
import { useCallback, useState } from 'react';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { IndexPage } from '../../../../components/index-page/IndexPage';
import type { ColumnDef, IndexQueryState } from '../../../../components/index-page/types';
import type { MenuItem } from '../../../../components/Menu';
import { StatusText } from '../../../../components/table/StatusText';
import { TextCell } from '../../../../components/table/TextCell';
import { ApiError } from '../../../../lib/apiError';
import { formatDateOnly } from '../../../../lib/formatDate';
import { formatMoney } from '../../../../lib/formatMoney';
import { SalaryGradeModal } from '../components/SalaryGradeModal';
import { salaryGradesApi } from '../plantillaApi';
import { salaryTypeLabel, type SalaryGradeListItem } from '../plantillaTypes';

/** The salary schedule a position's grade is read against. */
export function SalaryGradesIndexPage() {
  const [editing, setEditing] = useState<SalaryGradeListItem | null>(null);
  const [creating, setCreating] = useState(false);
  const [confirming, setConfirming] = useState<{
    kind: 'deactivate' | 'activate';
    grade: SalaryGradeListItem;
  } | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const refresh = useCallback(() => setReloadKey((k) => k + 1), []);

  const fetchGrades = useCallback(
    (query: IndexQueryState, signal: AbortSignal) =>
      salaryGradesApi.list(
        {
          search: query.search,
          page: query.page,
          pageSize: query.pageSize,
          sort: query.sort ?? undefined,
          status: query.facets.status?.join(','),
          type: query.facets.type?.join(','),
        },
        signal,
      ),
    [],
  );

  const rowActions = useCallback(
    (grade: SalaryGradeListItem): MenuItem[] => [
      {
        key: `restore-${grade.id}`,
        label: 'Restore',
        icon: <RotateCcw className="h-4 w-4" aria-hidden="true" />,
        permission: 'salary-grades.delete',
        disabledReason: grade.isActive ? 'This salary grade is already active.' : null,
        onClick: () => setConfirming({ kind: 'activate', grade }),
      },
    ],
    [],
  );

  async function runConfirmed() {
    if (!confirming) return;

    setRunning(true);
    setError(null);

    try {
      if (confirming.kind === 'deactivate') await salaryGradesApi.deactivate(confirming.grade.id);
      else await salaryGradesApi.activate(confirming.grade.id);

      setConfirming(null);
      refresh();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : new ApiError('The action could not be completed.', 0));
    } finally {
      setRunning(false);
    }
  }

  return (
    <>
      <IndexPage<SalaryGradeListItem>
        module="salary-grades"
        breadcrumb={[
          'Human Resources',
          { label: 'Organization', to: '/organization/chart' },
          { label: 'Settings', to: '/organization/settings' },
          'Salary Grades',
        ]}
        columns={COLUMNS}
        facets={[
          { key: 'status', label: 'Status' },
          { key: 'type', label: 'Type' },
        ]}
        /*
          Filed by type, not by status. A rate is read for which salary type it belongs to —
          General or Health — and the two are what a reader switches between; status is still
          a rail facet and Show deleted still lifts it, exactly as on every other module.
        */
        folderNavFacet="type"
        auditEntityTypes={['SalaryGrade']}
        rowKey={(grade) => grade.id}
        fetch={fetchGrades}
        reloadKey={reloadKey}
        searchPlaceholder="Search grades"
        emptyMessage="No salary grades yet. Add the rates a plantilla position's grade is read against."
        onCreate={() => setCreating(true)}
        createPermission="salary-grades.create"
        rowActions={rowActions}
        onEdit={(grade) => setEditing(grade)}
        editPermission="salary-grades.update"
        onDelete={(grade) => setConfirming({ kind: 'deactivate', grade })}
        deletePermission="salary-grades.delete"
        deleteLabel={() => 'Delete'}
        deleteDisabledReason={(grade) => (grade.isActive ? null : 'This salary grade is already deleted.')}
        onClearData={() => salaryGradesApi.clear()}
        clearDataPermission="salary-grades.clear-data"
        otherInfo={(grade) => grade}
      />

      {creating || editing !== null ? (
        <SalaryGradeModal
          open
          editing={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={() => {
            setCreating(false);
            setEditing(null);
            refresh();
          }}
        />
      ) : null}

      {confirming ? (
        <ConfirmDialog
          open
          onClose={() => {
            setConfirming(null);
            setError(null);
          }}
          onConfirm={() => void runConfirmed()}
          title={confirming.kind === 'deactivate' ? 'Delete salary grade' : 'Restore salary grade'}
          danger={confirming.kind === 'deactivate'}
          loading={running}
          id={`salary-grade-${confirming.kind}-dialog`}
          body={
            <div className="flex flex-col gap-2">
              <p>
                {confirming.kind === 'deactivate'
                  ? `Grade ${confirming.grade.grade} step ${confirming.grade.step} will stop being offered when a position is given a grade. Positions already carrying it keep it — a position records the grade, not this rate.`
                  : `Grade ${confirming.grade.grade} step ${confirming.grade.step} will be offered again.`}
              </p>
              {error ? (
                <p role="alert" className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700">
                  {error.message}
                </p>
              ) : null}
            </div>
          }
        />
      ) : null}
    </>
  );
}

const COLUMNS: ColumnDef<SalaryGradeListItem>[] = [
  {
    key: 'grade',
    header: 'Grade',
    width: 110,
    sortable: true,
    render: (grade) => <TextCell value={`SG ${grade.grade}`} />,
  },
  {
    key: 'step',
    header: 'Step',
    width: 90,
    align: 'right',
    sortable: true,
    summary: true,
    render: (grade) => <TextCell value={String(grade.step)} mono />,
  },
  {
    key: 'type',
    header: 'Type',
    width: 120,
    summary: true,
    render: (grade) => <TextCell value={salaryTypeLabel(grade.type)} />,
  },
  {
    key: 'tranche',
    header: 'Tranche',
    width: 110,
    align: 'right',
    sortable: true,
    render: (grade) => <TextCell value={String(grade.tranche)} mono />,
  },
  {
    key: 'monthlyAmount',
    header: 'Monthly rate',
    width: 150,
    align: 'right',
    sortable: true,
    summary: true,
    render: (grade) => <TextCell value={formatMoney(grade.monthlyAmount)} mono />,
  },
  {
    key: 'effectivityDate',
    header: 'Effective from',
    width: 150,
    sortable: true,
    // A calendar date, not an instant — <DateCell> would shift it across a zone boundary.
    render: (grade) => <TextCell value={formatDateOnly(grade.effectivityDate)} mono />,
  },
  {
    key: 'description',
    header: 'Description',
    width: 240,
    flex: true,
    render: (grade) => <TextCell value={grade.description} />,
  },
  {
    key: 'status',
    header: 'Status',
    width: 120,
    status: true,
    render: (grade) =>
      grade.isActive ? (
        <StatusText tone="active">Active</StatusText>
      ) : (
        <StatusText tone="inactive">Deleted</StatusText>
      ),
  },
];
